// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

// Import Material UI
import Dialog from '@material-ui/core/Dialog';
import { Close } from '@material-ui/icons';
import CircularProgress from '@material-ui/core/CircularProgress';
import Brightness1Icon from "@material-ui/icons/Brightness1";
import { Button } from "@material-ui/core";
import Alert from '@material-ui/lab/Alert';
import Chip from "@material-ui/core/Chip";

// Import Pages
import { alertState } from '../../../../../recoil-store/recoil-state';
import DataTable from "../../../../../components/DataTable/DataTable";

// Import Helpers
import { sleep } from '../../../../../helpers/helper';

// Import Services
import LibraryService from '../../../../../services/LibraryService/LibraryService';

// Import CSS
import './RequiredLibraries.css';

const default_lib_image = `data:image/png;base64,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`

function RequiredLibraries({ data, handleClose, downloadBot }) {

    const { t } = useTranslation();
    const [libraries, setLibraries] = useState();
    const [information, setInformation] = useState(); // type => info,error,warning, visible:true,message:''

    const [customColumn, setCustomColumn] = useState([]);

    const setAlert = useSetRecoilState(alertState);

    const label = 'CONTENT.BOT-DETAILS.REQUIRED-LIBRARY.';
    const messageLabel = 'MESSAGES.STORE.';

    useEffect(() => {
        async function checkRequiredLibraries() {
            setLibraries(data.required_libraries);
            setInformation({ status: true, severity: 'info', message: t(label + 'CHECKING-DETECTED'), circular: true });
            const result = await LibraryService.checkNonExistingLibraries({ required_libraries: data.required_libraries });
            if (!result.status) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(messageLabel + result.message),
                    severity: 'error'
                }))
            } else {
                if (result.data && result.data.length) {
                    setTimeout(() => {
                        setLibraries(result.data);
                        if (result.data.find(x => x.is_system)) {
                            setInformation({ status: true, severity: 'warning', message: t(label + 'UPGRADE-VERSION'), additional_messsage: t(label + 'MINIMUM-PRODUCT-VERSION') + data.product_version });
                        } else {
                            setInformation({ status: true, severity: 'warning', message: t(label + 'DETECTED'), button: true, button_text: t(label + 'DOWNLOAD-LIBRARIES') });
                        }
                    }, 5000)

                    // setOpenRequiredLibraries({ open: true, data: result.data });
                } else {
                    setInformation({ status: true, severity: 'info', message: t(label + 'ALL-LIBRARIES-EXIST'), circular: true });
                    downloadBot((response) => {
                        setInformation({ status: true, severity: response.status ? 'success' : 'error', message: response.message });
                        setTimeout(() => {
                            handleClose();
                        }, 3000)
                    });
                    // kullanılan tüm kütüphaneler sistemde mevcut.
                    // Bot indirme işlemi başlatılıyor.
                }
            }

        }

        checkRequiredLibraries().then(r => r);
    }, []);

    const getStateColor = (state) => {
        let color;
        switch (state) {
            case 'waiting':
                color = "#ff9800";
                // sarı
                break;
            case 'success':
                color = "#43AA41";
                // yeşil
                break;
            case 'error':
                color = "#D45D5E";
                // kırmızı
                break;
            case 'inprogress':
                color = "#2196f3";
                // mavi
                break;
            default:
                color = "#2196f3";
                break;
        }
        return color;
    }

    useEffect(() => {
        if (libraries) {
            const column = [
                {
                    id: 0,
                    name: "ids",
                    label: " ",
                    options: {
                        filter: false,
                        sort: false,
                        display: true
                    },
                }, {
                    id: 1,
                    label: " ",
                    name: "status",
                    options: {
                        filter: false,
                        sort: false,
                        customBodyRenderLite: (dataIndex, rowIndex) => {
                            if (libraries[dataIndex]) {
                                return (
                                    <div key={0}>
                                        <Brightness1Icon
                                            style={{
                                                width: 13,
                                                height: 13,
                                                color: getStateColor(libraries[dataIndex].state),
                                            }}
                                        />
                                    </div>
                                )
                            }
                        },
                    }
                },
                {
                    id: 2,
                    name: "library_name",
                    label: t("CONTENT.LIBRARIES.LIBRARY-NAME"),
                    options: {
                        filter: true,
                        sort: false,
                        customBodyRenderLite: (dataIndex) => {
                            return (
                                <div style={{ marginRight: 5 }}>
                                    {
                                        <div className="libraries-row">
                                            <img alt="library_icon"
                                                src={default_lib_image}
                                                width="18px" height="18px" />
                                            {libraries[dataIndex]?.label.includes('CUSTOM') ? libraries[dataIndex]?.name :
                                                t(
                                                    "CONTENT.LIBRARY." +
                                                    libraries[dataIndex]?.name +
                                                    ".NAME"
                                                )}
                                        </div>
                                    }

                                </div>
                            );
                        },
                    },
                },
                {
                    id: 3,
                    name: "is_system",
                    label: t(label + "IS-SYSTEM-LIBRARY"),
                    options: {
                        filter: true, sort: false, display: libraries.find(x => x.hasOwnProperty('library_exist')) ? true : false,
                        customBodyRenderLite: (dataIndex) => {
                            if (libraries[dataIndex]) {
                                return (<div>{t(label + (libraries[dataIndex].is_system ? "YES" : "NO"))}</div>)
                            }
                        }
                    }
                },
                {
                    id: 4,
                    name: "library_exist",
                    label: t(label + "INSTALLED-LIBRARY"),
                    options: {
                        filter: true, sort: false, display: libraries.find(x => x.hasOwnProperty('library_exist')) ? true : false,
                        customBodyRenderLite: (dataIndex) => {
                            if (libraries[dataIndex]) {
                                return (<div>{t(label + (libraries[dataIndex].library_exist ? "YES" : "NO"))}</div>)
                            }
                        }
                    }
                },
                {
                    id: 5,
                    name: "existing_version",
                    label: t(label + "EXIST-VERSION"),
                    options: {
                        filter: true, sort: false, display: libraries.find(x => x.hasOwnProperty('library_exist')) ? true : false,
                        customBodyRenderLite: (dataIndex) => {
                            if (libraries[dataIndex]) {
                                if (libraries[dataIndex].existing_version) {
                                    return (
                                        <div>
                                            <Chip style={{ marginRight: "5px" }} color="primary" size="small"
                                                label={`v${libraries[dataIndex].existing_version}`} />
                                            {libraries[dataIndex].active_version &&
                                                <Chip size="small" label={t(label + 'ACTIVE')} />}
                                        </div>
                                    )
                                } else {
                                    return <div>-</div>
                                }

                            }
                        }
                    }
                },
                {
                    id: 6,
                    name: "version",
                    label: t(label + "MANDATORY-VERSION"),
                    options: {
                        filter: true,
                        sort: false,
                        customBodyRenderLite: (dataIndex) => {
                            if (libraries[dataIndex]) {
                                return (
                                    <div>
                                        <Chip style={{ marginRight: "5px" }} color="secondary" size="small"
                                            label={`v${libraries[dataIndex].version}`} />
                                    </div>
                                )
                            }
                        },
                    },
                },
            ]
            setCustomColumn(column)
        }
    }, [libraries])

    const downloadLibraries = async () => {
        setInformation({ status: true, severity: 'info', message: t(label + 'DOWNLOADING'), circular: true });
        let copyLibraries = [...libraries];
        for (let library of copyLibraries) {
            if (!library.downloaded) {
                library.state = 'inprogress';
                setLibraries(copyLibraries);
                await sleep(1000)
                const response = await LibraryService.downloadCustomLibraryByLabel({ library_label: library.label, version: library.version });
                if (response) {
                    if (response.status === true) {
                        library.downloaded = true;
                        library.library_exist = true;
                        library.existing_version = library.version;
                        library.is_active = true;
                        library.state = 'success';
                        setLibraries([...copyLibraries]);
                    } else {
                        library.state = 'error';
                        setInformation({ status: true, severity: 'error', message: t(label + 'DOWNLOAD-ERROR') + t("MESSAGES.LIBRARY." + response.message), button: true, button_text: t(label + 'RETRY-DOWNLOAD') });
                        setLibraries([...copyLibraries]);
                        return;
                    }
                } else {
                    library.state = 'error';
                    setInformation({ status: true, severity: 'error', message: t(label + 'DOWNLOAD-ERROR') + t('GENERAL.NETWORK-ERROR'), button: true, button_text: t(label + 'RETRY-DOWNLOAD') });
                    setLibraries([...copyLibraries]);
                    return;
                }
            }

        }
        setInformation({ status: true, severity: 'success', message: t(label + 'SUCCESSFULLY-DOWNLOADED') });
        await sleep(5000);
        setInformation({ status: true, severity: 'info', message: t(label + 'BOT-DOWNLOADING') });
        await sleep(1000);
        downloadBot((response) => {
            setInformation({ status: true, severity: response.status ? 'success' : 'error', message: response.message });
            setTimeout(() => {
                handleClose();
            }, 5000)
        });
    }

    return (
        <Dialog open={true} maxWidth="md" fullWidth={true}>
            <div className='required-library-container'>
                <div className='required-library-header'>
                    <div className="bot-dialog-close">
                        <Close fontSize="small" onClick={() => handleClose(false)} />
                    </div>

                </div>
                <div className='required-library-content'>
                    <div className='description'></div>
                    {information && information.status &&
                        <Alert icon={information.circular ? <CircularProgress className='required-loading-circular-process' /> : undefined} severity={information.severity}>
                            <div>{information.message + (information.additional_messsage ? information.additional_messsage : '')}</div>
                            {
                                information.button &&
                                <Button className='download-library-button' onClick={downloadLibraries}>{information.button_text}</Button>
                            }
                        </Alert>
                    }

                    <DataTable
                        data={libraries}
                        customColumns={customColumn}
                        title={t("MESSAGES.MONITORING.AUTOM-MONITORING")}
                        loading={false}
                    />
                </div>
            </div>

        </Dialog>
    );
}

export default RequiredLibraries;
